import { getSession } from '../api';

// ── Order Tracking Service ─────────────────────────────────────────────────────
// Persists product orders to localStorage keyed by user ID. Each order carries
// a delivery status timeline with timestamps, tracking number, carrier, and
// estimated delivery date. This is browser-local (not cross-device) — consistent
// with the existing localStorage patterns used by the marketplace.

const STORAGE_KEY = 'em_orders';

// Delivery stages in order
export const DELIVERY_STAGES = [
  { key: 'ordered',          label: 'Order Placed',     icon: 'FiShoppingBag',   color: 'blue'    },
  { key: 'processing',       label: 'Processing',       icon: 'FiPackage',       color: 'amber'   },
  { key: 'shipped',           label: 'Shipped',          icon: 'FiTruck',         color: 'indigo'  },
  { key: 'out_for_delivery', label: 'Out for Delivery', icon: 'FiClock',         color: 'orange'  },
  { key: 'delivered',        label: 'Delivered',        icon: 'FiCheckCircle',   color: 'green'   },
];

const CARRIERS = [
  { id: 'standard',  label: 'Standard Courier',  etaDays: 3 },
  { id: 'express',   label: 'Express Delivery',  etaDays: 1 },
  { id: 'same_day',  label: 'Same-Day Delivery',  etaDays: 0 },
  { id: 'pickup',    label: 'In-Store Pickup',    etaDays: 0 },
  { id: 'funeral',   label: 'Funeral Home Drop',  etaDays: 2 },
];

function getStorageKey() {
  const user = getSession();
  const userId = user?.id || 'guest';
  return `${STORAGE_KEY}_${userId}`;
}

function readOrders() {
  try {
    return JSON.parse(localStorage.getItem(getStorageKey()) || '[]');
  } catch {
    return [];
  }
}

function writeOrders(orders) {
  try {
    localStorage.setItem(getStorageKey(), JSON.stringify(orders));
  } catch { /* private mode */ }
}

function generateOrderId() {
  // Server-assigned IDs are ideal, but orders are localStorage-only here.
  // Use a readable prefix + timestamp + random suffix for uniqueness.
  const ts = Date.now().toString(36);
  const rand = Math.random().toString(36).slice(2, 8);
  return `ORD-${ts}-${rand}`.toUpperCase();
}

function generateTrackingNumber(carrierId) {
  const prefix = carrierId === 'express' ? 'EXP' : carrierId === 'same_day' ? 'SDY' : carrierId === 'pickup' ? 'PKP' : carrierId === 'funeral' ? 'FHD' : 'STD';
  const num = Math.floor(100000000000 + Math.random() * 900000000000);
  return `${prefix}${num}`;
}

function calcEtaDays(carrierId) {
  const carrier = CARRIERS.find(c => c.id === carrierId);
  return carrier ? carrier.etaDays : 3;
}

function calcEstimatedDelivery(carrierId) {
  const days = calcEtaDays(carrierId);
  const date = new Date();
  date.setDate(date.getDate() + days);
  return date.toISOString();
}

// ── Public API ─────────────────────────────────────────────────────────────────

export const orderTrackingService = {
  CARRIERS,
  DELIVERY_STAGES,

  /**
   * Create a new order from cart checkout data.
   * @param {Object} checkout - { items, total, paymentResult, giftOptions, specialInstructions, carrierId }
   * @returns {Object} The created order record.
   */
  createOrder(checkout) {
    const user = getSession();
    const now = new Date().toISOString();
    const carrierId = checkout.carrierId || 'standard';
    const orderId = generateOrderId();
    const trackingNumber = generateTrackingNumber(carrierId);
    const estimatedDelivery = calcEstimatedDelivery(carrierId);

    const order = {
      orderId,
      userId: user?.id || 'guest',
      customerName: user?.name || 'Guest',
      customerEmail: user?.email || '',
      items: checkout.items || [],
      total: checkout.total || 0,
      currency: checkout.currency || 'USD',
      payment: {
        gateway: checkout.paymentResult?.gateway || 'unknown',
        transactionId: checkout.paymentResult?.transactionId || '',
        paidAt: now,
      },
      giftOptions: checkout.giftOptions || null,
      specialInstructions: checkout.specialInstructions || '',
      delivery: {
        carrierId,
        carrierLabel: (CARRIERS.find(c => c.id === carrierId) || {}).label || 'Standard Courier',
        trackingNumber,
        estimatedDelivery,
        recipientName: checkout.giftOptions?.recipientName || user?.name || 'Guest',
        address: checkout.giftOptions?.address || '',
      },
      timeline: [
        {
          stage: 'ordered',
          label: 'Order Placed',
          timestamp: now,
          description: 'Your order has been confirmed and payment received.',
        },
      ],
      currentStage: 'ordered',
      createdAt: now,
      updatedAt: now,
    };

    const orders = readOrders();
    orders.unshift(order);
    writeOrders(orders);

    // Also store a merchant-visible copy keyed by merchant IDs in items
    this._syncMerchantOrders(order);

    return order;
  },

  /**
   * Get all orders for the current user (newest first).
   */
  getOrders() {
    return readOrders().sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
  },

  /**
   * Get a single order by orderId.
   */
  getOrder(orderId) {
    return readOrders().find(o => o.orderId === orderId) || null;
  },

  /**
   * Advance the delivery status to the next stage.
   */
  advanceStage(orderId) {
    const orders = readOrders();
    const order = orders.find(o => o.orderId === orderId);
    if (!order) return null;

    const currentIdx = DELIVERY_STAGES.findIndex(s => s.key === order.currentStage);
    if (currentIdx >= DELIVERY_STAGES.length - 1) return order; // already at final stage

    const nextStage = DELIVERY_STAGES[currentIdx + 1];
    const now = new Date().toISOString();

    order.currentStage = nextStage.key;
    order.timeline.push({
      stage: nextStage.key,
      label: nextStage.label,
      timestamp: now,
      description: this._stageDescription(nextStage.key),
    });
    order.updatedAt = now;

    writeOrders(orders);
    this._syncMerchantOrders(order);
    return order;
  },

  /**
   * Set the delivery stage directly (for merchant overrides).
   */
  setStage(orderId, stageKey) {
    const orders = readOrders();
    const order = orders.find(o => o.orderId === orderId);
    if (!order) return null;

    const stage = DELIVERY_STAGES.find(s => s.key === stageKey);
    if (!stage) return null;

    const now = new Date().toISOString();
    order.currentStage = stageKey;

    // Don't add duplicate timeline entries
    if (!order.timeline.find(t => t.stage === stageKey)) {
      order.timeline.push({
        stage: stageKey,
        label: stage.label,
        timestamp: now,
        description: this._stageDescription(stageKey),
      });
    }
    order.updatedAt = now;

    writeOrders(orders);
    this._syncMerchantOrders(order);
    return order;
  },

  /**
   * Update tracking number and carrier on an order.
   */
  updateTracking(orderId, trackingNumber, carrierId) {
    const orders = readOrders();
    const order = orders.find(o => o.orderId === orderId);
    if (!order) return null;

    order.delivery.trackingNumber = trackingNumber || order.delivery.trackingNumber;
    if (carrierId) {
      order.delivery.carrierId = carrierId;
      order.delivery.carrierLabel = (CARRIERS.find(c => c.id === carrierId) || {}).label || order.delivery.carrierLabel;
      order.delivery.estimatedDelivery = calcEstimatedDelivery(carrierId);
    }
    order.updatedAt = new Date().toISOString();

    writeOrders(orders);
    this._syncMerchantOrders(order);
    return order;
  },

  /**
   * Get all orders across all users (merchant/admin view).
   */
  getAllOrders() {
    const all = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith(`${STORAGE_KEY}_`)) {
        try {
          const parsed = JSON.parse(localStorage.getItem(key) || '[]');
          if (Array.isArray(parsed)) all.push(...parsed);
        } catch { /* skip */ }
      }
    }
    return all.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
  },

  /**
   * Get orders containing items from a specific merchant.
   */
  getMerchantOrders(merchantId) {
    return this.getAllOrders().filter(order =>
      order.items.some(item => String(item.merchantId || item.vendor_id || '') === String(merchantId))
    );
  },

  _stageDescription(stageKey) {
    const descriptions = {
      ordered:          'Your order has been confirmed and payment received.',
      processing:       'The merchant is preparing your items for dispatch.',
      shipped:          'Your order has been dispatched and is on its way.',
      out_for_delivery: 'The courier is out for delivery — expect it today.',
      delivered:        'Your order has been delivered. Thank you for your purchase.',
    };
    return descriptions[stageKey] || '';
  },

  // Keep a merchant-visible copy in a separate key so merchants can see orders
  _syncMerchantOrders(order) {
    try {
      const merchantKey = 'em_merchant_orders';
      const all = JSON.parse(localStorage.getItem(merchantKey) || '[]');
      const idx = all.findIndex(o => o.orderId === order.orderId);
      if (idx >= 0) all[idx] = order;
      else all.unshift(order);
      localStorage.setItem(merchantKey, JSON.stringify(all));
    } catch { /* private mode */ }
  },
};

export default orderTrackingService;